import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PointLight2D draws nothing on the canvas, as in Godot: it adds its cookie to
 * the accumulation buffer every lit canvas item multiplies its albedo against
 * (`r3f/lighting2d/CanvasLighting2D`). CanvasItem2D supplies transform, `visible` and z.
 */
import { useLayoutEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { CanvasItem2D } from '../../../r3f/components/CanvasItem2D';
import { useTexture2D } from '../../../resources/useTexture2D';
import { useTextureView } from '../../../r3f/useTextureView';
import { useSceneResources } from '../../../r3f/SceneResourcesContext';
import { MissingResourcePlaceholder } from '../../../r3f/components/MissingResourcePlaceholder';
import { updateShadowPolarTexture, shadowColorContributes, } from '../../../r3f/lighting2d/lightQuad';
import { buildShadowPolarMap, shadowMapZFarInv } from '../../../r3f/lighting2d/shadowPolarMap';
import { useLightSequence } from '../../../r3f/lighting2d/useLightSequence';
import { lightDrawSequence } from '../../../r3f/lighting2d/lightSequence';
import { usePassMeshRef, useRegisterCanvasLight2D } from '../../../r3f/lighting2d/CanvasLighting2D';
import { DEFAULT_LIGHT_CULL_KEY } from '../../../r3f/lighting2d/lightCullKey';
import { useLightShadowCasters } from '../../../r3f/lighting2d/ShadowCasterStage';
import { useShadowLightPose } from '../../../r3f/lighting2d/shadowLightPose';
import { litQuadRenderOrder, ShadowVolumeMask } from '../../../r3f/lighting2d/ShadowVolumeMask';
import { useLightQuadMaterials } from './useLightQuadMaterials';
import { placePositionalLight, useApartLightOrigin } from '../../../r3f/lighting2d/apartLight';
import { useDisposeOnRelease } from '../../../r3f/useDisposeOnRelease';
import { Light2DBlendMode, Light2DShadowFilter } from '../../../godot/scene/light2d.js';
/** The quads a light draws besides its lit quad, each only when its material exists. */
const OPTIONAL_ROLES = [
    'shade',
    'tint',
    'fade',
    'unshadowed',
    'unshadowedFade',
];
/**
 * Godot resolves a canvas item's DEFAULT repeat to the viewport's DISABLED default
 * (`viewport.h:419-420`, `renderer_canvas_render_rd.cpp:2344`), and the light-texture
 * tap uses that item sampler (`canvas.glsl:774-782`). The cookie states clamp and never
 * inherits a Repeat from the shared producer; a clamp-tagged entry is reused as-is.
 */
const COOKIE_SAMPLER = { colorSpace: THREE.NoColorSpace, wrap: THREE.ClampToEdgeWrapping };
export function PointLight2D({ node, children }) {
    const props = node.properties;
    const { externalResources, internalResources } = useSceneResources();
    // The cookie is often a GradientTexture2D, a radial falloff described inline,
    // not an image. `useTexture2D` resolves either kind.
    const { texture: cookieSource, missing } = useTexture2D(props.texture, externalResources, internalResources);
    // A light that draws no quad makes no view: it would clone and upload a cookie nothing draws.
    const cookie = useTextureView(props.enabled && !missing ? cookieSource : null, COOKIE_SAMPLER);
    // With no texture the light adds nothing and draws no placeholder: measured with `pnpm ref:godot`.
    const lights = props.enabled && !!cookieSource;
    // `canvas.glsl:806` shadows only an item whose `light_mask` meets `shadow_item_cull_mask`. With
    // no reached item meeting it, the shadow lands nowhere, so the light casts nothing.
    const shadowReachesItem = (props.range_item_cull_mask & props.shadow_item_cull_mask) !== 0;
    const shadowEnabled = props.shadow_enabled && shadowReachesItem;
    const casters = useLightShadowCasters(shadowEnabled, props.shadow_item_cull_mask);
    const casts = casters.length > 0;
    // The cull tuple, not the node's own `light_mask` (its CanvasItem mask), picks the items this
    // light reaches. Not memoised: the declaration is compared by value.
    const reach = {
        ...DEFAULT_LIGHT_CULL_KEY,
        itemCullMask: props.range_item_cull_mask,
        zMin: props.range_z_min,
        zMax: props.range_z_max,
        layerMin: props.range_layer_min,
        layerMax: props.range_layer_max,
    };
    // Godot's default shadow_color is transparent (`light_2d.h:58`), so the extra albedo-free pass
    // runs only for the rare light that tints its shadow.
    const tintsShadow = casts && shadowColorContributes(props.shadow_color, props.shadow_filter);
    // Two jobs, two numbers: `ordinal` keeps the shadow stencil stamps of one pass apart (dense,
    // reused on unmount), while `sequence` is this light's position in the canvas light list, which
    // is what Godot applies lights in (`renderer_canvas_render_rd.cpp:2365-2384`) and what
    // order-dependent MIX depends on.
    const listed = useLightSequence();
    const ordinal = useRegisterCanvasLight2D(lights, {
        reach,
        sequence: lightDrawSequence(listed, null),
        shadowItemCullMask: casts ? props.shadow_item_cull_mask : null,
        tintsShadow,
        mixes: props.blend_mode === Light2DBlendMode.MIX,
    });
    const sequence = lightDrawSequence(listed, ordinal);
    // An item the light reaches through a bit outside `shadow_item_cull_mask` takes it unshadowed.
    const reachesUnshadowedItem = (props.range_item_cull_mask & ~props.shadow_item_cull_mask) !== 0;
    // A normal-mapped item shades the light from where its node sits.
    const setOrigin = useApartLightOrigin(ordinal, placePositionalLight, props.height);
    // A disabled light still draws its children: `enabled` switches the light alone.
    return (_jsx(CanvasItem2D, { node: node, props: props, body: () => !props.enabled ? null : missing ? (_jsx(MissingResourcePlaceholder, { shape: "plane", name: node.name })) : cookie ? (_jsxs(_Fragment, { children: [_jsx("object3D", { ref: setOrigin }), _jsx(QuadMesh, { cookie: cookie, color: props.color, energy: props.energy, scale: props.texture_scale, offset: props.offset, blendMode: props.blend_mode, shadowColor: props.shadow_color, shadowFilter: props.shadow_filter, shadowFilterSmooth: props.shadow_filter_smooth, ordinal: ordinal, sequence: sequence, tintsShadow: tintsShadow, reachesUnshadowedItem: reachesUnshadowedItem, casters: casters })] })) : null, children: children }));
}
/**
 * The cookie quad, the unshadowed quad and the `shadow_color` quad share this component, so their
 * geometry and offset cannot drift. Only their role and material differ. Complementary stencil
 * tests keep the cookie and `shadow_color` quads off each other's pixels, and the pass draws the
 * unshadowed quad only for a list on which the light is unshadowed.
 */
function LightQuad({ ordinal, role, onMesh, material, offset, width, height, sequence, }) {
    const meshRef = usePassMeshRef(ordinal, role, onMesh);
    return (_jsx("mesh", { ref: meshRef, position: [offset.x, -offset.y, 0], material: material, 
        // Explicit rather than left to three's depth sort, because the volume mask
        // has to land between this quad and the previous light's.
        renderOrder: litQuadRenderOrder(sequence), children: _jsx("planeGeometry", { args: [width, height] }) }));
}
function QuadMesh({ cookie, color, energy, scale, offset, blendMode, shadowColor, shadowFilter, shadowFilterSmooth, ordinal, sequence, tintsShadow, reachesUnshadowedItem, casters, }) {
    const width = cookie.image?.width ?? 1;
    const height = cookie.image?.height ?? 1;
    // A callback ref, not useRef, so the pose sampler starts once the quad is in
    // the tree: the world matrix it needs does not exist before that.
    const [quad, setQuad] = useState(null);
    const light = useShadowLightPose(quad, casters.length > 0);
    const shadowed = !!light && casters.length > 0;
    // `shadow_filter` picks the mechanism. NONE stencils: the volumes stamp and the
    // cookie draws only where they did not, as Godot's transparent default
    // `shadow_color` adds nothing there. PCF5/PCF13 sample the polar map through
    // Godot's tap kernel, for its stepped penumbra.
    const filtered = shadowed && shadowFilter !== Light2DShadowFilter.NONE;
    // Built only for a filtered light, so an unfiltered one runs none of that path.
    // It is a pure function of the pose and the casters, so it rebuilds exactly
    // when the volumes do.
    const bins = useMemo(() => (filtered && light ? buildShadowPolarMap(light, casters) : null), [filtered, light, casters]);
    // The texture outlives each rebuild: both quad materials hold its identity in
    // `uShadowMap`, so an occluder settling during load refills it rather than
    // rebuilding two ShaderMaterials. The refill runs at commit, since a write from
    // a render React discards would reach the materials already on screen.
    const [shadowMap, setShadowMap] = useState(null);
    useLayoutEffect(() => {
        if (!bins) {
            setShadowMap(null);
            return;
        }
        const next = updateShadowPolarTexture(shadowMap, bins);
        if (next !== shadowMap)
            setShadowMap(next);
    }, [bins, shadowMap]);
    // Keyed on the texture, not on mount, so a light that stops being shadowed
    // (its occluders hidden, or the sub-scene carrying them swapped out) gives
    // the GL object back instead of holding it for the session.
    useDisposeOnRelease(shadowMap);
    const sampling = useMemo(() => {
        if (!shadowMap || !light)
            return undefined;
        const [m00, m01, m02, m10, m11, m12] = light.worldToLocal;
        return {
            map: shadowMap,
            // Anything the parser admits that is not NONE takes the wider kernel only
            // at PCF13; `shadow_filter` is a three-value enum, so this is exhaustive.
            filter: shadowFilter === Light2DShadowFilter.PCF13 ? Light2DShadowFilter.PCF13 : Light2DShadowFilter.PCF5,
            smooth: shadowFilterSmooth,
            worldToLocal: new THREE.Matrix3().set(m00, m01, m02, m10, m11, m12, 0, 0, 1),
            zFarInv: shadowMapZFarInv(light.radius),
            shadowColor,
        };
    }, [shadowMap, light, shadowFilter, shadowFilterSmooth, shadowColor]);
    const materials = useLightQuadMaterials({
        cookie,
        color,
        energy,
        blendMode,
        shadowColor,
        tintsShadow,
        shadowed,
        filtered,
        sampling,
        ordinal,
        drawsUnshadowed: casters.length > 0 && reachesUnshadowedItem,
    });
    const shape = { ordinal, offset, width: width * scale, height: height * scale, sequence };
    return (_jsxs(_Fragment, { children: [shadowed && !filtered && ordinal !== null && (_jsx(ShadowVolumeMask, { light: light, casters: casters, ordinal: ordinal, sequence: sequence })), _jsx(LightQuad, { role: "lit", onMesh: setQuad, material: materials.lit, ...shape }), OPTIONAL_ROLES.map((role) => materials[role] && _jsx(LightQuad, { role: role, material: materials[role], ...shape }, role))] }));
}
//# sourceMappingURL=Component.js.map